import React, { useCallback } from 'react';
import { withRouter } from 'react-router-dom';

import ChangePasswordPanel from 'components/login/ChangePasswordPanel.component';
import EmailPanel from 'components/login/EmailPanel.component';
import ResetErrorPanel from 'components/login/ResetErrorPanel.component';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useAfn from 'hooks/useAfn.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { withRcmPasswordReset } from 'utils/cognito.util';
import fromAnyToErrorMessage from 'utils/converters/any/fromAnyToErrorMessage.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';

import CN from './ForgotPasswordPage.module.scss';

const CTAG = 'ForgotPasswordPage';
const L = logger(CTAG);

const EMAIL_SUBTITLE = "We couldn't send you a reset code, if you already have one, you can continue";

const STATE = Object.freeze({
  emailInput: 'please provide email',
  emailError: 'there was email error',
  codeInput: 'please enter the code',
  codeError: 'there was code/password error',
  done: 'successfully changed the password',
});

const ForgotPasswordPage = (props) => {
  const { sendVerificationCode, setPassword } = props;

  const nav = navFor('ForgotPasswordPage', props);

  const [email, setEmail] = useStateWhileMounted(S.empty);
  const [state, setState] = useStateWhileMounted(STATE.emailInput);
  const [error, setError] = useStateWhileMounted(S.empty);

  const handleEmailError = useCallback(() => void setState(STATE.codeInput), [setState]);
  const handleCodeError = useCallback(() => void setState(STATE.emailInput), [setState]);

  const [{ isLoading: isEmailLoading }, handleEmailInput] = useAfn(
    async ({ email }) => {
      try {
        if (isEmailLoading) {
          L.warn('handleEmailInput()', { isEmailLoading, email });
          return;
        }
        setEmail(email);
        await sendVerificationCode(email);
        setState(STATE.codeInput);
        setError(S.empty);
      } catch (e) {
        L.warn('handleEmail()', e);
        setState(STATE.emailError);
        setError(e.code === 'UserNotFoundException' ? 'User not found' : fromAnyToErrorMessage(e));
      }
    },
    [sendVerificationCode, setError, setState, setEmail],
  );

  const [{ isLoading: isPassLoading }, handleCodeInput] = useAfn(
    async ({ code, pass }) => {
      try {
        if (isPassLoading) {
          L.warn('handleCodeInput()', { isPassLoading, code });
          return;
        }
        await setPassword(email, code, pass);
        setError(S.empty);
        setState(S.done);
        nav.push({ pathname: LINK.login });
      } catch (e) {
        L.warn('handleCodeInput()', e);
        setState(STATE.codeError);
        setError(fromAnyToErrorMessage(e));
      }
    },
    [email, nav, setError, setPassword, setState],
  );

  const isLoading = isEmailLoading || isPassLoading;

  L.info('()', state);

  return (
    <div data-bem="ForgotPasswordPage" className={CN.component}>
      <div className={CN.illustrationTop} />
      <div className={CN.illustrationBottom} />
      <div className={CN.panel} data-bem="ForgotPasswordPage__body">
        {state === STATE.emailInput && <EmailPanel tag={CTAG} onNext={handleEmailInput} isLoading={isLoading} />}
        {state === STATE.emailError && (
          <ResetErrorPanel tag={CTAG} onNext={handleEmailError} error={error} subtitle={EMAIL_SUBTITLE} />
        )}
        {state === STATE.codeInput && <ChangePasswordPanel onNext={handleCodeInput} isLoading={isLoading} />}
        {state === STATE.codeError && <ResetErrorPanel tag={CTAG} onNext={handleCodeError} error={error} />}
        {state === STATE.codeError && <div />}
      </div>
    </div>
  );
};

const ForgotPasswordPageHoC = withRcmPasswordReset(withRouter(ForgotPasswordPage));

// noinspection JSUnusedGlobalSymbols
export default ForgotPasswordPageHoC;
